import { useState, useEffect, useRef } from 'react';

import CornerGripperLogo from '../components/CornerGripperLogo';
import { ScanResult } from '../constants/containerManagerInterfaces';
import Dialog from '../components/Dialog';
import Locktube, { LOCKTUBE } from '../vodjs/Locktube';
import { elasticLogEvent } from '../vodjs/ElasticLog';
import { getRobotId } from '../vodjs/lib';

import './LoadCancelScreen.scss';
import { getConfig } from '../utils/configUtils';

export interface LoadCancelScreenProps {
  tapToReload: () => void;
  lastScanTimestamp: string;
  scanResult: string;
  drawerStatus: string;
  clickCancel: () => void;
  readyToCancel: boolean;
  taskUuid: string;
}

const usePrevious = (value: string) => {
  const ref = useRef<string>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

export default function LoadCancelScreen(props: LoadCancelScreenProps) {
  const{
    tapToReload,
    lastScanTimestamp,
    scanResult,
    drawerStatus,
    clickCancel,
    readyToCancel,
    taskUuid
  } = props;

  // const robotId = getRobotId();
  const [showProhibitedModal, setShowProhibitedModal] = useState(false);
  const [showCloseDrawers, setShowCloseDrawers] = useState(false);

  const lastScan = usePrevious(lastScanTimestamp);
  const supportNumber = getConfig().site?.supportNumber;

  useEffect(() => {
    if (lastScan !== lastScanTimestamp) {
      setShowProhibitedModal(scanResult === ScanResult.Prohibited);
    }
  }, [scanResult, lastScan, lastScanTimestamp]);

  useEffect(() => {
    if(drawerStatus === LOCKTUBE.unloaded || readyToCancel) {
      // check if readyToCancel intervened
      if(readyToCancel && drawerStatus != LOCKTUBE.unloaded) {
          elasticLogEvent({ what: 'readyToCancel', msg: `Task ${taskUuid} was canceled due to readyToCancel`, taskUuid: taskUuid });
      }
      // drawers have been closed
      clickCancel();  // cancel task
    }
    else if(drawerStatus === LOCKTUBE.unloading) {
      // drawers are popped
      setShowCloseDrawers(true);
    }
    else {
      // LOCKTUBE.wait_for_scan or loading
      // badge has not been scanned yet (drawers are closed)
      setShowCloseDrawers(false);
    }
  }, [drawerStatus, clickCancel, readyToCancel]);

  const getTitle = () => {
    return `Please Unload Drawers`;
  };

  const getSubtitle = () => {
    if(showCloseDrawers) return 'Confirm all items have been removed and close the drawers to finish canceling this delivery.';
    else return 'Scan your badge to open the drawers for the canceled delivery and confirm all items have been removed.';
  };

  return (
    <div className="load-cancel-screen">
      <CornerGripperLogo tapToReload={tapToReload} />
      <div className="load-cancel-screen__header">
        <div className="header__title-wrapper">
          <span className="header__title-text">{getTitle()}</span>
        </div>
      </div>
      <div className={`load-cancel-screen__content`}>
        <h3 className="content__subtitle-text">
          {getSubtitle()}
        </h3>
        {!showCloseDrawers && (
          <div className="content__badge-reader">
            <img draggable="false" src={require('../assets/images/moxi-badge-reader.gif')} alt="Moxi badge scan" />
          </div>
        )}
      </div>
      <div className="load-cancel-screen__footer">
      </div>
      {showProhibitedModal && (
        <Dialog
          title="You can't access this delivery."
          text={`Your badge doesn't have permission to access what this Moxi is
          carrying. Contact Moxi support ${supportNumber ? `at ${supportNumber} ` : ''}for help.`}
          primaryButtonText="OKAY"
          primaryButtonColor={'#0091ae'}
          primaryButtonWidth={220}
          clickPrimaryButton={() => setShowProhibitedModal(false)}
        />
      )}
    </div>
  );
}